import React, { useCallback, useEffect, useState } from 'react';
import client from '../../api/client';
import './AdminPages.css';

const PAGE_LABELS = {
  welcome: 'Welcome',
  vera: 'VERA',
  analytics: 'Analytics',
  'customer-data': 'Customer Data',
  overview: 'Overview',
  filters: 'Filters',
  facilities: 'Facilities',
  automations: 'Automations',
  'admin.users': 'Admin · Users',
  'admin.roles': 'Admin · Roles',
  'admin.customers': 'Admin · Customers'
};

function RoleMatrix() {
  const [matrix, setMatrix] = useState({});
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get('/api/admin/roles/matrix');
      setRoles(res.data?.roles || []);
      setPages(res.data?.pages || []);
      const map = {};
      (res.data?.matrix || []).forEach(r => {
        map[`${r.pageKey}|${r.role}`] = { canView: r.canView, canEdit: r.canEdit };
      });
      setMatrix(map);
      setDirty(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (page, role, field) => {
    if (role === 'Owner') return;
    const key = `${page}|${role}`;
    const current = matrix[key] || { canView: false, canEdit: false };
    const next = { ...current, [field]: !current[field] };
    if (field === 'canView' && !next.canView) next.canEdit = false;
    if (field === 'canEdit' && next.canEdit) next.canView = true;
    setMatrix({ ...matrix, [key]: next });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updates = [];
      for (const page of pages) {
        for (const role of roles) {
          if (role === 'Owner') continue;
          const entry = matrix[`${page}|${role}`] || { canView: false, canEdit: false };
          updates.push({ pageKey: page, role, canView: entry.canView, canEdit: entry.canEdit });
        }
      }
      await client.put('/api/admin/roles/matrix', updates);
      setDirty(false);
      setMessage('Saved');
      setTimeout(() => setMessage(''), 2500);
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-loading">Loading permissions matrix…</div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h2>Role & Permission Matrix</h2>
          <p>Configure which roles can view and edit each page. Owner always has full access.</p>
        </div>
        <div className="admin-header-actions">
          {message && <span className="admin-ok">{message}</span>}
          <button className="admin-primary-btn" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="admin-matrix-wrap">
        <table className="admin-matrix">
          <thead>
            <tr>
              <th>Page</th>
              {roles.map(r => (
                <th key={r} colSpan={r === 'Owner' ? 1 : 2}>
                  {r}{r === 'Owner' && ' (all)'}
                </th>
              ))}
            </tr>
            <tr className="admin-matrix-subhead">
              <th />
              {roles.map(r => (r === 'Owner' ? (
                <th key={`${r}-all`}>—</th>
              ) : (
                <React.Fragment key={r}>
                  <th>View</th>
                  <th>Edit</th>
                </React.Fragment>
              )))}
            </tr>
          </thead>
          <tbody>
            {pages.map(p => (
              <tr key={p}>
                <td className="admin-matrix-page">{PAGE_LABELS[p] || p}</td>
                {roles.map(r => {
                  if (r === 'Owner') return <td key={`${p}-${r}`}>✓</td>;
                  const entry = matrix[`${p}|${r}`] || { canView: false, canEdit: false };
                  return (
                    <React.Fragment key={`${p}-${r}`}>
                      <td>
                        <input type="checkbox" checked={entry.canView} onChange={() => toggle(p, r, 'canView')} />
                      </td>
                      <td>
                        <input type="checkbox" checked={entry.canEdit} onChange={() => toggle(p, r, 'canEdit')} />
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RoleMatrix;
